import { randomId } from '@aha-app/mvc';

/**
 * Registry of handlers for special item types
 */
export const specialItemHandlers: Record<
  string,
  (node: Element, parentObj: any) => boolean
> = {
  'nav-item': (node: Element, parentObj: any): boolean => {
    // This is a nav-item within a navigation component
    const itemId = randomId();
    const isActive = node.getAttribute('active') === 'true';
    const icon = node.getAttribute('icon') || undefined;
    const value = node.textContent?.trim() || 'Menu Item';

    // Initialize itemsData if it doesn't exist
    if (!parentObj.itemsData) {
      parentObj.itemsData = {};
    }

    // Calculate position based on existing items
    const itemCount = Object.keys(parentObj.itemsData).length;
    const itemPosition = itemCount * 1000000;

    // Add to parent's itemsData
    parentObj.itemsData[itemId] = {
      id: itemId,
      value: value,
      item_position: itemPosition,
      ...(isActive && { state: 'selected' }),
      ...(icon && { icon: icon }),
    };

    return true;
  },

  'dropdown-option': (node: Element, parentObj: any): boolean => {
    // This is a dropdown-option within a dropdown component
    const itemId = randomId();
    const isSelected = node.getAttribute('selected') === 'true';
    const value = node.textContent?.trim() || 'Option';

    // Initialize itemsData if it doesn't exist
    if (!parentObj.itemsData) {
      parentObj.itemsData = {};
    }

    // Calculate position based on existing items
    const itemCount = Object.keys(parentObj.itemsData).length;
    const itemPosition = itemCount * 1000000;

    // Add to parent's itemsData
    parentObj.itemsData[itemId] = {
      id: itemId,
      value: value,
      item_position: itemPosition,
      ...(isSelected && { state: 'selected' }),
    };

    return true;
  },

  'breadcrumb-item': (node: Element, parentObj: any): boolean => {
    // This is a breadcrumb-item within a breadcrumb component
    const itemId = randomId();
    const isActive = node.getAttribute('active') === 'true';
    const icon = node.getAttribute('icon') || undefined;
    const value = node.textContent?.trim() || 'Item';

    // Initialize itemsData if it doesn't exist
    if (!parentObj.itemsData) {
      parentObj.itemsData = {};
    }

    // Calculate position based on existing items
    const itemCount = Object.keys(parentObj.itemsData || {}).length;
    const itemPosition = itemCount * 1000000;

    // Add to parent's itemsData
    parentObj.itemsData[itemId] = {
      id: itemId,
      value: value,
      item_position: itemPosition,
      ...(isActive && { state: 'default' }),
      ...(icon && { icon: icon }),
    };

    return true;
  },
};

/**
 * Checks if a node is a special item type and processes it if so
 *
 * @param node The XML element to process
 * @param parentObj The parent object that will contain this special item
 * @param specialItemTags List of tags this parent can handle as special items
 * @returns true if the node was processed as a special item, false otherwise
 */
export function processSpecialItem(
  node: Element,
  parentObj: any,
  specialItemTags?: string[]
): boolean {
  const nodeName = node.tagName.toLowerCase();

  if (!specialItemTags?.includes(nodeName)) {
    return false;
  }

  const handler = specialItemHandlers[nodeName];
  if (handler) {
    return handler(node, parentObj);
  }

  return false;
}
